import { CloseCircleFilled, DeleteOutlined, EditOutlined, UploadOutlined } from "@ant-design/icons";
import { Popconfirm, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { orderApi } from "api/order.api";
import { staffApi } from "api/staff.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { ImportExcelModal, ImportExcelResult } from "components/Import/ImportExcelModal";
import {
  ActionButton,
  CodeCell,
  CustomerCell,
  FailReason,
  StatusCell,
  TableContent,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { userStore } from "store/userStore";
import { Order, OrderCounts, OrderDebt, OrderDebtFilter, OrderDebtStatus, OrderStatus } from "types/order";
import { QueryParam } from "types/query";
import { Staff } from "types/staff";
import { can } from "utils/auth";
import { formatMoney, QUOTE_PATH } from "views/Opportunity/opportunity.meta";
import { StatusTabs } from "views/Opportunity/components/StatusTabs";
import {
  ORDER_PATH,
  collectedAmountTone,
  debtFilterOptions,
  debtStatusLabel,
  emptyOrderCounts,
  isDebtOverdue,
  orderStatusTrans,
  orderTabs,
  toDisplayDate,
} from "../order.meta";
import "../Order.scss";

const countOf = (counts: OrderCounts, key: string) => {
  if (key === "ALL") return counts.all;
  return counts.statuses[key as OrderStatus] || 0;
};

type OrderListProps = {
  reloadKey: number;
  onOpen: (order: Order) => void;
};

type StaffOption = { label: string; value: number };

const CUSTOMER_PATH = "/customer";

const renderDebts = (orderDebts?: OrderDebt[]) => {
  if (!orderDebts?.length) return <TableContent>--</TableContent>;
  return (
    <div className="orderDebtLines">
      {orderDebts.map((orderDebt) => {
        const isPaid = orderDebt.status === OrderDebtStatus.Paid;
        const overdue = isDebtOverdue(orderDebt);

        let dateSuffix = "";
        if (isPaid) {
          if (orderDebt.collectedDate) dateSuffix = ` (${toDisplayDate(orderDebt.collectedDate)})`;
        } else if (orderDebt.dueDate) {
          dateSuffix = ` (dự kiến ${toDisplayDate(orderDebt.dueDate)})`;
        }

        const text = `${orderDebt.content || "--"} · ${formatMoney(orderDebt.amount || 0)} · ${
          debtStatusLabel[orderDebt.status] || "--"
        }${dateSuffix}`;

        const toneClass = isPaid
          ? "orderDebtLine--paid"
          : overdue
            ? "orderDebtLine--overdue"
            : "orderDebtLine--pending";

        return (
          <TableContent key={orderDebt.id} className={toneClass}>
            {text}
          </TableContent>
        );
      })}
    </div>
  );
};

export const OrderList = observer(({ reloadKey, onOpen }: OrderListProps) => {
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("ALL");
  const [query, setQuery] = useState<QueryParam>({ page: 1, limit: 50, search: "" });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<OrderCounts>(emptyOrderCounts());
  const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
  const [importKind, setImportKind] = useState<"order" | "debt" | null>(null);
  const selectedStaffRef = useRef<StaffOption>();
  const staffOptionsRef = useRef<StaffOption[]>([]);
  staffOptionsRef.current = staffOptions;

  const canEdit = can(ORDER_PATH, "UPDATE");
  const canDelete = can(ORDER_PATH, "DELETE");
  const isAdmin = !!userStore.info.role?.isAdmin;
  const onlyMine = !isAdmin && can(CUSTOMER_PATH, "ONLY_MINE");

  const fetchData = useCallback(async (queryParams: QueryParam, tabKey: string) => {
    setLoading(true);
    try {
      const params: QueryParam = { ...queryParams };
      if (tabKey !== "ALL") params.status = tabKey;
      // Tab "Tất cả" không gồm đơn đóng và thất bại, xem ở các tab "Đóng", "Thất bại"
      else params.excludeClosed = "true";
      const res = await orderApi.findAll(params);
      setData(res.data.orders || []);
      setTotal(res.data.total || 0);
      setCounts(res.data.counts || emptyOrderCounts());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(query, tab);
  }, [fetchData, query, reloadKey, tab]);

  const debounceSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        setQuery((prev) => ({ ...prev, search: keyword, page: 1 }));
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debounceSearch.cancel();
    };
  }, [debounceSearch]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      debounceSearch(value);
    },
    [debounceSearch],
  );

  const handleClearSearch = useCallback(() => {
    setSearch("");
    debounceSearch.cancel();
    setQuery((prev) => ({ ...prev, page: 1, search: "" }));
  }, [debounceSearch]);

  const handleTabChange = useCallback((key: string) => {
    setTab(key);
    setQuery((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handlePageChange = useCallback(({ limit, page }: { limit: number; page: number }) => {
    setQuery((prev) => ({ ...prev, page, limit }));
  }, []);

  const fetchStaff = useCallback(async (keyword: string) => {
    const res = await staffApi.findAll({ page: 1, limit: 20, search: keyword });
    const staffs: Staff[] = res.data.staffs || [];
    const options = staffs.map((staff) => ({
      label: staff.name || staff.username,
      value: staff.id,
    }));
    const selected = selectedStaffRef.current;
    if (selected && !options.some((option) => option.value === selected.value)) {
      setStaffOptions([selected, ...options]);
      return;
    }
    setStaffOptions(options);
  }, []);

  const debounceSearchStaff = useMemo(
    () =>
      debounce((keyword: string) => {
        fetchStaff(keyword);
      }, 500),
    [fetchStaff],
  );

  useEffect(() => {
    if (onlyMine) {
      return;
    }
    fetchStaff("");
    return () => {
      debounceSearchStaff.cancel();
    };
  }, [debounceSearchStaff, fetchStaff, onlyMine]);

  const handleStaffChange = useCallback((value: string | number | undefined) => {
    const staffId = typeof value === "number" ? value : undefined;
    selectedStaffRef.current = staffId
      ? staffOptionsRef.current.find((option) => option.value === staffId)
      : undefined;
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (staffId) {
        next.staffId = staffId;
      } else {
        delete next.staffId;
      }
      return next;
    });
  }, []);

  const refresh = useCallback(() => {
    fetchData(query, tab);
  }, [fetchData, query, tab]);

  const closeImport = useCallback(() => {
    setImportKind(null);
  }, []);

  const importOrderFile = useCallback(async (file: File) => {
    const res = await orderApi.importExcel(file);
    return (res.data.results || []) as ImportExcelResult[];
  }, []);

  const importDebtFile = useCallback(async (file: File) => {
    const res = await orderApi.importDebtExcel(file);
    return (res.data.results || []) as ImportExcelResult[];
  }, []);

  const handleDebtFilterChange = useCallback((value: string | number | undefined) => {
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (value) {
        next.debtStatus = value;
      } else {
        delete next.debtStatus;
      }
      return next;
    });
  }, []);

  const toolbarFilters = useMemo(
    () => [
      ...(onlyMine
        ? []
        : [
          {
            key: "staffId",
            value: (query.staffId as number | undefined) ?? "",
            options: staffOptions,
            onChange: handleStaffChange,
            placeholder: "Sale",
            allowClear: true,
            showSearch: true,
            filterOption: false as const,
            onSearch: debounceSearchStaff,
            width: 220,
          },
        ]),
      {
        key: "debtStatus",
        value: (query.debtStatus as OrderDebtFilter | undefined) ?? "",
        options: debtFilterOptions,
        onChange: handleDebtFilterChange,
        placeholder: "Công nợ",
        allowClear: true,
        width: 180,
      },
    ],
    [debounceSearchStaff, handleDebtFilterChange, handleStaffChange, onlyMine, query.debtStatus, query.staffId, staffOptions],
  );

  const openDetail = useCallback(
    (order: Order) => {
      onOpen(order);
    },
    [onOpen],
  );

  const stopRowClick = useCallback((event: MouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleDelete = useCallback(
    async (order: Order) => {
      setLoading(true);
      try {
        await orderApi.delete(order.id);
        message.success("Xóa đơn hàng thành công");
        await fetchData(query, tab);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, query, tab],
  );

  const tabs = orderTabs.map((item) => ({
    ...item,
    count: countOf(counts, item.key),
  }));

  const columns = useMemo<TableColumnsType<Order>>(
    () => [
      {
        title: "Mã đơn",
        dataIndex: "code",
        key: "orderCode",
        width: 120,
        render: (value: string) => <CodeCell value={value || "--"} />,
      },
      {
        title: "Mã hợp đồng",
        dataIndex: "contractCode",
        key: "contractCode",
        width: 140,
        render: (value: string) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Mã báo giá",
        key: "code",
        width: 130,
        render: (_, record) => {
          const code = record.opportunity?.code;
          const opportunityId = record.opportunity?.id;
          if (!code || !opportunityId) return <TableContent>--</TableContent>;


          return (
            <a
              className="orderQuoteLink"
              href={`${QUOTE_PATH}/${opportunityId}`}
              target="_blank"
              rel="noreferrer"
              onClick={stopRowClick}
            >
              <CodeCell value={code} />
            </a>
          );
        },
      },
      {
        title: "Khách hàng",
        key: "customer",
        width: 220,
        render: (_, record) => <CustomerCell customer={record.customer} />,
      },
      {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
        width: 200,
        render: (_: unknown, record: Order) => (
          <div>
            <StatusCell value={record.status} trans={orderStatusTrans} />
            {record.status === OrderStatus.Failed && <FailReason reason={record.failReason} />}
          </div>
        ),
      },
      {
        title: "Tổng tiền",
        key: "totalAmount",
        width: 140,
        align: "right",
        render: (_, record) => (
          <TableContent>{formatMoney(record.opportunity?.totalAmountWithVat ?? record.opportunity?.totalAmount ?? 0)}</TableContent>
        ),
      },
      {
        title: "Đã thu",
        key: "collectedAmount",
        width: 140,
        align: "right",
        render: (_, record) => {
          const collected = record.collectedAmount || 0;
          const orderAmount = record.opportunity?.totalAmountWithVat ?? record.opportunity?.totalAmount ?? 0;
          const tone = collectedAmountTone(collected, orderAmount);
          return <TableContent className={`orderMoney orderMoney--${tone}`}>{formatMoney(collected)}</TableContent>;
        },
      },
      {
        title: "Công nợ",
        key: "orderDebts",
        width: 280,
        render: (_, record) => renderDebts(record.orderDebts),
      },
      ...(canEdit || canDelete
        ? [
          {
            title: "Thao tác",
            key: "action",
            width: 120,
            align: "center" as const,
            render: (_: unknown, record: Order) => (
              <div className="flex gap-2 justify-center items-center" onClick={stopRowClick}>
                {canEdit && (
                  <Tooltip title="Cập nhật">
                    <ActionButton aria-label="Cập nhật" onClick={() => openDetail(record)}>
                      <EditOutlined />
                    </ActionButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Xóa">
                    <Popconfirm
                      cancelText="Không"
                      okText="Xóa"
                      onConfirm={() => handleDelete(record)}
                      title="Xác nhận xóa đơn hàng?"
                    >
                      <ActionButton danger aria-label="Xóa">
                        <DeleteOutlined />
                      </ActionButton>
                    </Popconfirm>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]
        : []),
    ],
    [canDelete, canEdit, handleDelete, openDetail, stopRowClick],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  return (
    <div className="orderPage">
      <StatusTabs items={tabs} activeKey={tab} onChange={handleTabChange} />
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              width: 420,
              placeholder: "Tìm theo mã đơn, mã hợp đồng, mã báo giá, khách hàng",
              onChange: handleSearchChange,
              inputProps: {
                allowClear: {
                  clearIcon: <CloseCircleFilled onClick={handleClearSearch} />,
                },
              },
            }}
            leftActions={
              isAdmin ? (
                <>
                  <AppButton icon={<UploadOutlined />} onClick={() => setImportKind("order")}>
                    Import Excel
                  </AppButton>
                  <AppButton icon={<UploadOutlined />} onClick={() => setImportKind("debt")}>
                    Import công nợ
                  </AppButton>
                </>
              ) : null
            }
            filterLabel={null}
            filters={toolbarFilters}
          />
        }
        table={
          <AppTable<Order>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
            onRow={(record) => ({
              onClick: () => openDetail(record),
            })}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={handlePageChange}
          />
        }
      />
      <ImportExcelModal
        open={importKind === "order"}
        title="Import đơn hàng"
        sampleUrl="/templates/crm_order.xlsx"
        sampleFileName="crm_order.xlsx"
        onClose={closeImport}
        onSuccess={refresh}
        importFile={importOrderFile}
      />
      <ImportExcelModal
        open={importKind === "debt"}
        title="Import công nợ"
        sampleUrl="/templates/crm_payment_order.xlsx"
        sampleFileName="crm_payment_order.xlsx"
        onClose={closeImport}
        onSuccess={refresh}
        importFile={importDebtFile}
      />
    </div>
  );
});
